import axios from "axios";
import { API_CONFIG } from "./api/config";
import { clearAccessToken, setAccessToken, setAccessTokenFromResponse } from "./authTokenStore";

const normalizeGatewayBaseUrl = (value) => {
    if (typeof value !== "string") {
        return "";
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return "";
    }
    const withoutTrailingSlashes = trimmed.replace(/\/+$/, "");
    return withoutTrailingSlashes.replace(/\/api$/i, "");
};

const API_BASE_URL =
    normalizeGatewayBaseUrl(process.env.REACT_APP_API_URL)
    || normalizeGatewayBaseUrl(process.env.REACT_APP_USER_SERVICE_URL)
    || "https://muzone.co";
const AUTH_REFRESH_URL = `${API_BASE_URL}/api/auth/refresh`;

const AUTH_REFRESH_SKIP_PATHS = [
    "/api/auth/refresh",
    "/api/auth/login",
    "/api/auth/register",
    "/api/auth/register/email/",
    "/api/auth/password-reset/",
    "/api/auth/logout",
    "/api/auth/sso/",
];

const AUTH_REFRESH_DEBUG_ENABLED = process.env.REACT_APP_AUTH_REFRESH_DEBUG === "true";

const REFRESH_LOCK_STORAGE_KEY = "muzone.auth.refresh.lock.v1";
const REFRESH_RESULT_STORAGE_KEY = "muzone.auth.refresh.result.v1";
const SESSION_TERMINATION_STORAGE_KEY = "muzone.auth.session.termination.v1";
const REFRESH_CHANNEL_NAME = "muzone.auth.refresh.channel.v1";
const REFRESH_LOCK_TTL_MS = 8_000;
const REFRESH_LOCK_RENEW_INTERVAL_MS = Math.max(1_000, Math.floor(REFRESH_LOCK_TTL_MS / 3));
const REFRESH_RESULT_TTL_MS = 12_000;
const REFRESH_WAIT_TIMEOUT_MS = REFRESH_LOCK_TTL_MS + 5_000;
const REFRESH_WAIT_POLL_MS = 75;

const REFRESH_CONCURRENT_CODE = "REFRESH_CONCURRENT_REQUEST";
const REFRESH_ORIGIN_REJECTED_CODE = "REFRESH_ORIGIN_REJECTED";
const REFRESH_SUSPENDED_CODE = "AUTH_REFRESH_SUSPENDED";
export const AUTH_SESSION_EXPIRED_EVENT = "muzone:auth-session-expired";

const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const parseJson = (value) => {
    if (typeof value !== "string" || value.trim() === "") {
        return null;
    }
    try {
        return JSON.parse(value);
    } catch (_) {
        return null;
    }
};

const readPersistedSessionTerminationAtStartup = () => {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const payload = parseJson(window.localStorage?.getItem(SESSION_TERMINATION_STORAGE_KEY));
        if (!payload || payload.type !== "session:terminated") {
            return null;
        }
        return payload;
    } catch (_) {
        return null;
    }
};

const persistedSessionTerminationAtStartup = readPersistedSessionTerminationAtStartup();

let refreshPromise = null;
let queuedRefreshRequestCount = 0;
let refreshAttemptCounter = 0;
let lastAuthExpiredSignalAt = 0;
let authRefreshEnabled = !persistedSessionTerminationAtStartup;
let authSessionEpoch = persistedSessionTerminationAtStartup ? 1 : 0;

if (persistedSessionTerminationAtStartup) {
    clearAccessToken();
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const hasWindow = () => typeof window !== "undefined";

const getLocalStorage = () => {
    if (!hasWindow()) {
        return null;
    }
    try {
        return window.localStorage;
    } catch (_) {
        return null;
    }
};

const createRefreshBroadcastChannel = () => {
    if (typeof BroadcastChannel === "undefined") {
        return null;
    }
    try {
        return new BroadcastChannel(REFRESH_CHANNEL_NAME);
    } catch (_) {
        return null;
    }
};

const refreshBroadcastChannel = createRefreshBroadcastChannel();

const debugRefresh = (message, payload) => {
    if (!AUTH_REFRESH_DEBUG_ENABLED) {
        return;
    }
    if (payload === undefined) {
        console.debug(`[auth-refresh] ${message}`);
        return;
    }
    console.debug(`[auth-refresh] ${message}`, payload);
};

const extractAccessToken = (data) => {
    if (!data || typeof data !== "object") {
        return null;
    }
    const tokenValue =
        (typeof data.accessToken === "string" && data.accessToken)
        || (typeof data.token === "string" && data.token)
        || null;
    if (!tokenValue) {
        return null;
    }
    const trimmed = tokenValue.trim();
    return trimmed.length > 0 ? trimmed : null;
};

const readStorageJson = (key) => {
    const storage = getLocalStorage();
    if (!storage) {
        return null;
    }
    return parseJson(storage.getItem(key));
};

const writeStorageJson = (key, value) => {
    const storage = getLocalStorage();
    if (!storage) {
        return;
    }
    try {
        storage.setItem(key, JSON.stringify(value));
    } catch (_) {
        // no-op
    }
};

const removeStorageItem = (key) => {
    const storage = getLocalStorage();
    if (!storage) {
        return;
    }
    try {
        storage.removeItem(key);
    } catch (_) {
        // no-op
    }
};

const buildSuspendedRefreshResult = () => ({
    ok: false,
    authExpired: true,
    status: null,
    code: REFRESH_SUSPENDED_CODE,
    accessToken: null,
});

const applyLocalSessionTermination = (detail = {}, { emitEvent = true } = {}) => {
    authRefreshEnabled = false;
    authSessionEpoch += 1;
    clearAccessToken();
    removeStorageItem(REFRESH_LOCK_STORAGE_KEY);
    removeStorageItem(REFRESH_RESULT_STORAGE_KEY);

    if (emitEvent && hasWindow()) {
        window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT, {
            detail: {
                reason: "session-ended",
                ...detail,
            },
        }));
    }
};

const handleSessionTerminationMessage = (payload) => {
    if (!payload || payload.type !== "session:terminated" || payload.owner === TAB_ID) {
        return;
    }
    applyLocalSessionTermination({ source: "cross-tab" });
};

if (refreshBroadcastChannel) {
    refreshBroadcastChannel.addEventListener("message", (event) => {
        handleSessionTerminationMessage(event?.data);
    });
}

if (hasWindow()) {
    window.addEventListener("storage", (event) => {
        if (event.key !== SESSION_TERMINATION_STORAGE_KEY || !event.newValue) {
            return;
        }
        handleSessionTerminationMessage(parseJson(event.newValue));
    });
}

export const suspendAuthRefresh = ({ broadcast = true } = {}) => {
    applyLocalSessionTermination({ source: "local" }, { emitEvent: false });

    if (!broadcast) {
        return;
    }

    const event = {
        type: "session:terminated",
        owner: TAB_ID,
        nonce: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        timestamp: Date.now(),
    };
    writeStorageJson(SESSION_TERMINATION_STORAGE_KEY, event);
    if (refreshBroadcastChannel) {
        try {
            refreshBroadcastChannel.postMessage(event);
        } catch (_) {
            // local storage remains the cross-tab fallback
        }
    }
};

export const resumeAuthRefresh = () => {
    authSessionEpoch += 1;
    authRefreshEnabled = true;
    removeStorageItem(SESSION_TERMINATION_STORAGE_KEY);
};

export const isAuthRefreshSuspended = () => !authRefreshEnabled;

const isValidLock = (lock, now = Date.now()) => {
    if (!lock || typeof lock !== "object") {
        return false;
    }
    if (typeof lock.expiresAt !== "number") {
        return false;
    }
    return lock.expiresAt > now;
};

const isForeignLockActive = (lock, now = Date.now()) => {
    return isValidLock(lock, now) && lock.owner !== TAB_ID;
};

const tryAcquireCrossTabRefreshLock = () => {
    const storage = getLocalStorage();
    if (!storage) {
        return { acquired: true, lock: null };
    }

    const now = Date.now();
    const currentLock = readStorageJson(REFRESH_LOCK_STORAGE_KEY);
    if (isForeignLockActive(currentLock, now)) {
        return { acquired: false, lock: currentLock };
    }

    const lock = {
        owner: TAB_ID,
        nonce: `${now}-${Math.random().toString(36).slice(2, 10)}`,
        createdAt: now,
        expiresAt: now + REFRESH_LOCK_TTL_MS,
    };

    writeStorageJson(REFRESH_LOCK_STORAGE_KEY, lock);
    const confirmedLock = readStorageJson(REFRESH_LOCK_STORAGE_KEY);
    if (confirmedLock?.owner === TAB_ID && confirmedLock?.nonce === lock.nonce) {
        return { acquired: true, lock };
    }
    if (isForeignLockActive(confirmedLock, now)) {
        return { acquired: false, lock: confirmedLock };
    }
    return { acquired: true, lock };
};

const releaseCrossTabRefreshLock = (lock) => {
    if (!lock) {
        return;
    }
    const currentLock = readStorageJson(REFRESH_LOCK_STORAGE_KEY);
    if (currentLock?.owner === TAB_ID && currentLock?.nonce === lock.nonce) {
        removeStorageItem(REFRESH_LOCK_STORAGE_KEY);
    }
};

const startCrossTabRefreshLockHeartbeat = (lock) => {
    if (!lock) {
        return () => {};
    }

    let active = true;
    let intervalId = null;

    const stop = () => {
        if (!active) {
            return;
        }
        active = false;
        if (intervalId) {
            clearInterval(intervalId);
        }
    };

    const renew = () => {
        if (!active) {
            return;
        }

        const currentLock = readStorageJson(REFRESH_LOCK_STORAGE_KEY);
        if (!currentLock) {
            return;
        }
        if (currentLock.owner !== TAB_ID || currentLock.nonce !== lock.nonce) {
            debugRefresh("lock-heartbeat-ended", {
                reason: "lock-no-longer-owned",
                lockNonce: lock.nonce,
            });
            stop();
            return;
        }

        const renewedLock = {
            ...currentLock,
            expiresAt: Date.now() + REFRESH_LOCK_TTL_MS,
        };
        writeStorageJson(REFRESH_LOCK_STORAGE_KEY, renewedLock);
        lock.expiresAt = renewedLock.expiresAt;
    };

    intervalId = setInterval(renew, REFRESH_LOCK_RENEW_INTERVAL_MS);
    renew();

    return stop;
};

const publishRefreshEvent = (event) => {
    if (!event || typeof event !== "object") {
        return;
    }
    if (refreshBroadcastChannel) {
        try {
            refreshBroadcastChannel.postMessage(event);
        } catch (_) {
            // no-op
        }
    }
    if (event.type === "refresh:finished") {
        writeStorageJson(REFRESH_RESULT_STORAGE_KEY, {
            type: event.type,
            nonce: event.nonce,
            owner: event.owner,
            ok: event.ok,
            authExpired: event.authExpired,
            status: event.status,
            code: event.code,
            timestamp: Date.now(),
        });
    }
};

const mapRefreshEventToResult = (event) => {
    if (!event || event.type !== "refresh:finished") {
        return null;
    }
    return {
        ok: Boolean(event.ok),
        authExpired: Boolean(event.authExpired),
        status: typeof event.status === "number" ? event.status : null,
        code: typeof event.code === "string" ? event.code : null,
        accessToken: extractAccessToken(event),
    };
};

const readRecentResultForNonce = (nonce) => {
    const result = readStorageJson(REFRESH_RESULT_STORAGE_KEY);
    if (!result || result.nonce !== nonce) {
        return null;
    }
    if (typeof result.timestamp !== "number") {
        return null;
    }
    if (Date.now() - result.timestamp > REFRESH_RESULT_TTL_MS) {
        return null;
    }
    return mapRefreshEventToResult(result);
};

const revalidateRefreshLockOwnership = async (lock, trigger) => {
    if (!lock) {
        return { lock: null, foreignResult: null };
    }

    const currentLock = readStorageJson(REFRESH_LOCK_STORAGE_KEY);
    if (
        isValidLock(currentLock)
        && currentLock.owner === TAB_ID
        && currentLock.nonce === lock.nonce
    ) {
        return {
            lock: {
                ...lock,
                expiresAt: currentLock.expiresAt,
            },
            foreignResult: null,
        };
    }

    if (isForeignLockActive(currentLock)) {
        debugRefresh("refresh-lock-ownership-lost", {
            trigger,
            expectedNonce: lock.nonce,
            lockOwner: currentLock.owner,
            lockNonce: currentLock.nonce,
        });
        const foreignResult = await waitForCrossTabRefresh(currentLock, trigger);
        if (foreignResult) {
            return { lock: null, foreignResult };
        }
    }

    return { lock: null, foreignResult: null };
};

const waitForCrossTabRefresh = async (foreignLock, trigger) => {
    if (!foreignLock || !isForeignLockActive(foreignLock)) {
        return null;
    }

    queuedRefreshRequestCount += 1;
    debugRefresh("join-cross-tab-refresh", {
        trigger,
        queueSize: queuedRefreshRequestCount,
        lockOwner: foreignLock.owner,
        lockNonce: foreignLock.nonce,
    });

    try {
        const existingResult = readRecentResultForNonce(foreignLock.nonce);
        if (existingResult) {
            return existingResult;
        }

        return await new Promise((resolve) => {
            let settled = false;

            const settle = (value) => {
                if (settled) {
                    return;
                }
                settled = true;
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                if (intervalId) {
                    clearInterval(intervalId);
                }
                if (storageListenerAttached && hasWindow()) {
                    window.removeEventListener("storage", onStorage);
                }
                if (channelListenerAttached && refreshBroadcastChannel) {
                    refreshBroadcastChannel.removeEventListener("message", onChannelMessage);
                }
                resolve(value);
            };

            const pollState = () => {
                const resultFromStorage = readRecentResultForNonce(foreignLock.nonce);
                if (resultFromStorage) {
                    settle(resultFromStorage);
                }
            };

            const onStorage = (event) => {
                if (event.key === REFRESH_RESULT_STORAGE_KEY || event.key === REFRESH_LOCK_STORAGE_KEY) {
                    pollState();
                }
            };

            const onChannelMessage = (messageEvent) => {
                const payload = messageEvent?.data;
                if (!payload || payload.type !== "refresh:finished") {
                    return;
                }
                if (payload.nonce !== foreignLock.nonce) {
                    return;
                }
                settle(mapRefreshEventToResult(payload));
            };

            let storageListenerAttached = false;
            let channelListenerAttached = false;
            if (hasWindow()) {
                window.addEventListener("storage", onStorage);
                storageListenerAttached = true;
            }
            if (refreshBroadcastChannel) {
                refreshBroadcastChannel.addEventListener("message", onChannelMessage);
                channelListenerAttached = true;
            }

            const intervalId = setInterval(pollState, REFRESH_WAIT_POLL_MS);
            const timeoutId = setTimeout(() => settle(null), REFRESH_WAIT_TIMEOUT_MS);

            pollState();
        });
    } finally {
        queuedRefreshRequestCount = Math.max(0, queuedRefreshRequestCount - 1);
    }
};

const isAuthExpiredStatus = (status) => status === 401 || status === 403;

const isConcurrentRefreshStatus = (status, code) => {
    return status === 409 && code === REFRESH_CONCURRENT_CODE;
};

const isRefreshOriginRejectedStatus = (status, code) => {
    return status === 403 && code === REFRESH_ORIGIN_REJECTED_CODE;
};

const verifyCookieSessionAvailability = async () => {
    for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
            await axios.get(`${API_BASE_URL}/api/auth/me`, {
                withCredentials: true,
                skipAuthRefresh: true,
                timeout: API_CONFIG.REQUEST_TIMEOUT_MS,
            });
            return true;
        } catch (error) {
            const status = error?.response?.status;
            if (isAuthExpiredStatus(status)) {
                await wait(125 * (attempt + 1));
                continue;
            }
            return true;
        }
    }
    return false;
};

export const signalAuthSessionExpired = (detail = {}) => {
    clearAccessToken();

    const now = Date.now();
    if (now - lastAuthExpiredSignalAt < 1000) {
        return;
    }
    lastAuthExpiredSignalAt = now;

    if (!hasWindow()) {
        return;
    }
    window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT, { detail }));
};

const applyForeignRefreshResult = (foreignResult) => {
    if (!foreignResult) {
        return;
    }

    if (!authRefreshEnabled) {
        clearAccessToken();
        return;
    }

    if (foreignResult.authExpired) {
        signalAuthSessionExpired({
            reason: "cross-tab-refresh-failed",
            status: foreignResult.status,
            code: foreignResult.code,
        });
        return;
    }

    if (foreignResult.ok) {
        if (foreignResult.accessToken) {
            setAccessToken(foreignResult.accessToken);
        } else {
            // Fallback when the foreign tab result is observed via storage poll only.
            clearAccessToken();
        }
        return;
    }

    // Mirror owner-tab behavior for non-expired refresh failures
    // (for example REFRESH_ORIGIN_REJECTED) to avoid stale bearer override.
    clearAccessToken();
};

const waitForInFlightRefresh = async (trigger) => {
    if (!refreshPromise) {
        return null;
    }

    queuedRefreshRequestCount += 1;
    debugRefresh("join-in-flight-refresh", {
        trigger,
        queueSize: queuedRefreshRequestCount,
    });

    try {
        return await refreshPromise;
    } finally {
        queuedRefreshRequestCount = Math.max(0, queuedRefreshRequestCount - 1);
    }
};

const executeRefreshRequest = async ({ trigger, lock }) => {
    const refreshAttemptId = ++refreshAttemptCounter;
    const attemptEpoch = authSessionEpoch;
    let activeLock = lock;

    if (!authRefreshEnabled) {
        return buildSuspendedRefreshResult();
    }

    if (activeLock) {
        const preStartOwnership = await revalidateRefreshLockOwnership(activeLock, `${trigger}:pre-start`);
        if (preStartOwnership.foreignResult) {
            applyForeignRefreshResult(preStartOwnership.foreignResult);
            return preStartOwnership.foreignResult;
        }
        activeLock = preStartOwnership.lock;
    }

    const lockNonce = activeLock?.nonce || null;
    debugRefresh("refresh-start", {
        refreshAttemptId,
        trigger,
        queueSize: queuedRefreshRequestCount,
        lockNonce,
    });

    let stopLockHeartbeat = () => {};
    if (activeLock) {
        publishRefreshEvent({
            type: "refresh:started",
            nonce: activeLock.nonce,
            owner: TAB_ID,
            timestamp: Date.now(),
        });
        stopLockHeartbeat = startCrossTabRefreshLockHeartbeat(activeLock);
    }

    let result = { ok: false, authExpired: false, status: null, code: null, accessToken: null };

    try {
        if (activeLock) {
            const preRequestOwnership = await revalidateRefreshLockOwnership(activeLock, `${trigger}:pre-request`);
            if (preRequestOwnership.foreignResult) {
                activeLock = null;
                applyForeignRefreshResult(preRequestOwnership.foreignResult);
                return preRequestOwnership.foreignResult;
            }
            activeLock = preRequestOwnership.lock;
            if (!activeLock) {
                stopLockHeartbeat();
            }
        }

        if (!authRefreshEnabled || attemptEpoch !== authSessionEpoch) {
            result = buildSuspendedRefreshResult();
            return result;
        }

        const response = await axios.post(AUTH_REFRESH_URL, null, {
            withCredentials: true,
            skipAuthRefresh: true,
            timeout: API_CONFIG.REQUEST_TIMEOUT_MS,
        });

        if (!authRefreshEnabled || attemptEpoch !== authSessionEpoch) {
            clearAccessToken();
            result = buildSuspendedRefreshResult();
            return result;
        }

        const accessToken = extractAccessToken(response?.data);
        setAccessTokenFromResponse(response?.data);
        result = {
            ok: true,
            authExpired: false,
            status: response?.status ?? null,
            code: null,
            accessToken,
        };
    } catch (error) {
        const status = error?.response?.status ?? null;
        const code = error?.response?.data?.code ?? null;

        if (isConcurrentRefreshStatus(status, code)) {
            const stillAuthenticated = await verifyCookieSessionAvailability();
            if (stillAuthenticated) {
                // We cannot read HttpOnly cookie access token into memory, so avoid keeping
                // a stale bearer token that can override recovered cookie auth.
                clearAccessToken();
            }
            result = {
                ok: stillAuthenticated,
                authExpired: !stillAuthenticated,
                status,
                code,
                accessToken: null,
            };
        } else {
            const originRejected = isRefreshOriginRejectedStatus(status, code);
            if (originRejected) {
                // When refresh is denied by origin policy, session cookies may still be valid.
                // Drop any in-memory bearer so callers can continue with cookie auth.
                clearAccessToken();
            }
            result = {
                ok: false,
                authExpired: isAuthExpiredStatus(status) && !originRejected,
                status,
                code,
                accessToken: null,
            };
        }
    } finally {
        stopLockHeartbeat();
        if (activeLock) {
            publishRefreshEvent({
                type: "refresh:finished",
                nonce: activeLock.nonce,
                owner: TAB_ID,
                ok: result.ok,
                authExpired: result.authExpired,
                status: result.status,
                code: result.code,
                accessToken: result.accessToken,
            });
        }
        releaseCrossTabRefreshLock(activeLock);
    }

    if (result.authExpired) {
        signalAuthSessionExpired({
            reason: "refresh-failed",
            status: result.status,
            code: result.code,
        });
    }

    debugRefresh("refresh-finish", {
        refreshAttemptId,
        trigger,
        queueSize: queuedRefreshRequestCount,
        ok: result.ok,
        authExpired: result.authExpired,
        status: result.status,
        code: result.code,
    });

    return result;
};

const resolveUrlFromRequest = (request) => {
    if (typeof request === "string") {
        return request;
    }
    if (!request || typeof request !== "object") {
        return "";
    }
    if (typeof request.url === "string") {
        return request.url;
    }
    return "";
};

export const shouldSkipAuthRefresh = (request) => {
    if (!request) {
        return true;
    }
    if (typeof request === "object" && request.skipAuthRefresh) {
        return true;
    }

    const url = resolveUrlFromRequest(request);
    if (!url) {
        return true;
    }
    return AUTH_REFRESH_SKIP_PATHS.some((path) => url.includes(path));
};

export const refreshAccessTokenWithStatus = async ({ trigger = "unknown", allowCrossTab = true } = {}) => {
    if (!authRefreshEnabled) {
        return buildSuspendedRefreshResult();
    }

    if (refreshPromise) {
        return waitForInFlightRefresh(trigger);
    }

    let lock = null;
    if (allowCrossTab) {
        while (!lock) {
            const lockAttempt = tryAcquireCrossTabRefreshLock();
            if (lockAttempt.acquired) {
                lock = lockAttempt.lock;
                break;
            }

            const foreignResult = await waitForCrossTabRefresh(lockAttempt.lock, trigger);
            if (foreignResult) {
                if (!authRefreshEnabled) {
                    return buildSuspendedRefreshResult();
                }
                applyForeignRefreshResult(foreignResult);
                return foreignResult;
            }

            if (refreshPromise) {
                return waitForInFlightRefresh(trigger);
            }

            const latestForeignLock = readStorageJson(REFRESH_LOCK_STORAGE_KEY);
            if (isForeignLockActive(latestForeignLock)) {
                debugRefresh("cross-tab-refresh-wait-extended", {
                    trigger,
                    lockOwner: latestForeignLock.owner,
                    lockNonce: latestForeignLock.nonce,
                });
            }
        }
    }

    refreshPromise = executeRefreshRequest({ trigger, lock }).finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
};

export const refreshAccessToken = async (options = {}) => {
    const result = await refreshAccessTokenWithStatus(options);
    return result.ok;
};
